import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/KeycloakContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import axios from '../api/axios';
import './NotificationBell.css';

const TIME_AGO = (dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

const NOTIFICATION_ICONS = {
  note: 'fas fa-sticky-note',
  follow_up_task: 'fas fa-calendar-check',
  lead_created: 'fas fa-user-plus',
  lead_updated: 'fas fa-user-edit',
};

function NotificationBell() {
  const { keycloak } = useAuth();
  const { workspaceId } = useWorkspace();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const bellRef = useRef(null);
  const panelRef = useRef(null);

  const fetchUnreadCount = useCallback(async () => {
    if (!keycloak?.token || !workspaceId) return;
    try {
      const res = await axios.get('/notifications/unread-count', {
        params: { workspace_id: workspaceId },
      });
      if (res.data.success) {
        setUnreadCount(res.data.count);
      }
    } catch {
      // silently ignore
    }
  }, [keycloak?.token, workspaceId]);

  const fetchNotifications = useCallback(async () => {
    if (!keycloak?.token || !workspaceId) return;
    setLoading(true);
    try {
      const res = await axios.get('/notifications', {
        params: {
          limit: 20,
          workspace_id: workspaceId,
        },
      });
      if (res.data.success) {
        const all = res.data.data || [];
        setNotifications(all);
        setUnreadCount(all.filter((n) => !n.is_read).length);
      }
    } catch {
      // silently ignore
    } finally {
      setLoading(false);
    }
  }, [keycloak?.token, workspaceId]);

  // Poll unread count every 10s and on workspace change
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 10000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Listen for real-time notifications, workspace lead/note/task mutations, and tab focus
  useEffect(() => {
    const handleNewNotification = (e) => {
      const notif = e.detail;
      if (
        notif?.workspace_id &&
        workspaceId &&
        String(notif.workspace_id) !== String(workspaceId)
      ) {
        return;
      }
      setUnreadCount((prev) => prev + 1);
      // Prepend to list if panel is open
      setNotifications((prev) => {
        const exists = prev.some((n) => n.id === notif.id);
        if (exists) return prev;
        return [notif, ...prev].slice(0, 20);
      });
    };

    // When any note, task, or lead change occurs in this workspace,
    // refresh unread count immediately so the bell badge updates without clicking.
    // Skip if the current user is the actor — they won't have a notification for their own action.
    const handleLeadChanged = (e) => {
      const { workspaceId: eventWsId, actorId } = e.detail || {};
      if (eventWsId && workspaceId && String(eventWsId) !== String(workspaceId)) return;
      if (actorId && keycloak?.subject && String(actorId) === String(keycloak.subject)) return;
      fetchUnreadCount();
    };

    // When socket reconnects, re-fetch unread count
    const handleSocketReconnect = () => {
      fetchUnreadCount();
    };

    // When user returns to tab, refresh unread count
    const handleFocus = () => {
      fetchUnreadCount();
    };

    window.addEventListener('new-notification', handleNewNotification);
    window.addEventListener('lead-changed', handleLeadChanged);
    window.addEventListener('socket-reconnected', handleSocketReconnect);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleFocus);

    return () => {
      window.removeEventListener('new-notification', handleNewNotification);
      window.removeEventListener('lead-changed', handleLeadChanged);
      window.removeEventListener('socket-reconnected', handleSocketReconnect);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleFocus);
    };
  }, [workspaceId, fetchUnreadCount]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      if (
        bellRef.current &&
        !bellRef.current.contains(e.target) &&
        panelRef.current &&
        !panelRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  const [panelPos, setPanelPos] = useState({ top: 48, right: 24 });

  const handleToggle = () => {
    if (!isOpen) {
      fetchNotifications();
      // Compute position now — ref is guaranteed to be attached at this point
      if (bellRef.current) {
        const rect = bellRef.current.getBoundingClientRect();
        setPanelPos({ top: rect.bottom + 6, right: window.innerWidth - rect.right });
      }
    }
    setIsOpen((prev) => !prev);
  };

  const handleMarkRead = async (notifId) => {
    try {
      await axios.patch(`/notifications/${notifId}/read`);
      setNotifications((prev) => prev.map((n) => (n.id === notifId ? { ...n, is_read: true } : n)));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {
      // silently ignore
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.patch('/notifications/read-all', { workspace_id: workspaceId });
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch {
      const unread = notifications.filter((n) => !n.is_read);
      await Promise.allSettled(unread.map((n) => axios.patch(`/notifications/${n.id}/read`)));
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    }
  };

  const handleClearAll = async () => {
    try {
      await axios.delete('/notifications/clear-all', {
        params: { workspace_id: workspaceId },
      });
      setNotifications([]);
      setUnreadCount(0);
    } catch {
      setNotifications([]);
      setUnreadCount(0);
    }
  };

  const filteredNotifications = notifications.filter((n) => {
    if (!workspaceId) return true;
    return String(n.workspace_id).trim() === String(workspaceId).trim();
  });

  const panel =
    isOpen &&
    createPortal(
      <div
        ref={panelRef}
        className="notif-panel"
        style={{ top: panelPos.top, right: panelPos.right }}
      >
        <div className="notif-panel-header">
          <h4>Notifications</h4>
          <div className="notif-header-actions">
            {unreadCount > 0 && (
              <button className="notif-mark-all" onClick={handleMarkAllRead}>
                Mark all read
              </button>
            )}
            {filteredNotifications.length > 0 && (
              <button className="notif-clear-all" onClick={handleClearAll}>
                Clear all
              </button>
            )}
          </div>
        </div>
        <div className="notif-panel-body">
          {loading ? (
            <div className="notif-empty">
              <i className="fas fa-spinner fa-spin" />
              <span>Loading...</span>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="notif-empty">
              <i className="fas fa-bell-slash" />
              <span>No notifications yet</span>
            </div>
          ) : (
            filteredNotifications.map((notif) => (
              <div
                key={notif.id}
                className={`notif-item ${!notif.is_read ? 'unread' : ''}`}
                onClick={() => {
                  if (!notif.is_read) handleMarkRead(notif.id);
                  if (notif.conferencing_url) {
                    window.open(notif.conferencing_url, '_blank', 'noopener,noreferrer');
                  } else if (notif.meeting_id) {
                    navigate(`/meetings?meetingId=${notif.meeting_id}`);
                  } else if (notif.lead_id) {
                    const params = new URLSearchParams({ leadId: notif.lead_id });
                    if (notif.source_type === 'note') params.set('noteId', notif.source_id);
                    else if (notif.source_type === 'follow_up_task')
                      params.set('taskId', notif.source_id);
                    navigate(`/customer-management?${params.toString()}`);
                  }
                  setIsOpen(false);
                }}
              >
                <div className="notif-icon">
                  <i className={NOTIFICATION_ICONS[notif.source_type] || 'fas fa-bell'} />
                </div>
                <div className="notif-content">
                  <div className="notif-title">{notif.title}</div>
                  <div className="notif-message">{notif.message}</div>
                  <div className="notif-time">{TIME_AGO(notif.created_at)}</div>
                </div>
                {!notif.is_read && <div className="notif-dot" />}
              </div>
            ))
          )}
        </div>
      </div>,
      document.body
    );

  return (
    <div className="notif-bell-wrapper" ref={bellRef}>
      <button
        className="notif-bell-btn"
        onClick={handleToggle}
        title="Notifications"
        aria-expanded={isOpen}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        <i className="fas fa-bell" />
        {unreadCount > 0 && (
          <span className="notif-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>
      {panel}
    </div>
  );
}

export default NotificationBell;
